/*
  TD motion toggle.

  Auto-moving carousels and marquees that run longer than five seconds need a
  way to stop them (WCAG 2.2.2 Pause, Stop, Hide). Each [data-td-motion] wrapper
  names the kind of motion it holds; the [data-td-motion-toggle] button inside
  it pauses or resumes that motion and swaps its accessible name and icon.

  Supported kinds:
  - marquee:  CSS animation, paused with a class on the wrapper
  - swiper:   Swiper autoplay on the [data-swiper] element inside the wrapper
  - flickity: Flickity autoPlay on the [data-td-motion-target] element inside the wrapper

  Visitors who ask for reduced motion start paused; the button still lets them play.
*/
(() => {
  const SELECTORS = {
    wrapper: '[data-td-motion]',
    toggle: '[data-td-motion-toggle]',
    swiper: '[data-swiper]',
    flickity: '[data-td-motion-target]',
  };
  const PAUSED_CLASS = 'td-motion-toggle--paused';
  const MARQUEE_PAUSED_CLASS = 'td-motion--paused';
  // Swiper and Flickity are initialised by other deferred scripts, so wait for them.
  const POLL_INTERVAL = 250;
  const POLL_MAX_ATTEMPTS = 40;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // The theme's sliders use the jQuery Flickity from vendors.js; window.Flickity can be
  // a second copy bundled by pxu-sections.js that does not know about them.
  const getFlickity = (el) => {
    const fromJQuery = window.jQuery && window.jQuery(el).data('flickity');
    if (fromJQuery) return fromJQuery;
    return window.Flickity && window.Flickity.data ? window.Flickity.data(el) : null;
  };

  // Each adapter resolves its player and reports/changes whether it is paused.
  const ADAPTERS = {
    marquee: {
      resolve: (wrapper) => wrapper,
      isPaused: (wrapper) => wrapper.classList.contains(MARQUEE_PAUSED_CLASS),
      pause: (wrapper) => wrapper.classList.add(MARQUEE_PAUSED_CLASS),
      play: (wrapper) => wrapper.classList.remove(MARQUEE_PAUSED_CLASS),
      onChange: () => {},
    },
    swiper: {
      resolve: (wrapper) => {
        const swiper = wrapper.querySelector(SELECTORS.swiper)?.swiper;
        return swiper && swiper.autoplay && swiper.params.autoplay ? swiper : null;
      },
      isPaused: (swiper) => !swiper.autoplay.running,
      pause: (swiper) => swiper.autoplay.stop(),
      play: (swiper) => swiper.autoplay.start(),
      // Swiper stops autoplay itself when the visitor swipes or uses the arrows.
      onChange: (swiper, callback) => {
        swiper.on('autoplayStart', callback);
        swiper.on('autoplayStop', callback);
      },
    },
    flickity: {
      resolve: (wrapper) => {
        const target = wrapper.querySelector(SELECTORS.flickity);
        const flickity = target && getFlickity(target);
        return flickity && flickity.player && flickity.options.autoPlay ? flickity : null;
      },
      isPaused: (flickity) => flickity.player.state === 'stopped',
      pause: (flickity) => flickity.stopPlayer(),
      play: (flickity) => flickity.playPlayer(),
      // Flickity stops its player on drag, arrow, dot and keyboard use; it has no
      // stop event, so re-check once its own handlers have run.
      onChange: (flickity, callback) => {
        const deferred = () => setTimeout(callback);
        flickity.on('uiChange', deferred);
        flickity.on('pointerDown', deferred);
      },
    },
  };

  const syncToggle = (toggle, paused) => {
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    toggle.classList.toggle(PAUSED_CLASS, paused);
  };

  const bind = (wrapper, toggle, adapter, player) => {
    const sync = () => syncToggle(toggle, adapter.isPaused(player));

    if (prefersReducedMotion.matches) adapter.pause(player);
    sync();

    toggle.addEventListener('click', () => {
      if (adapter.isPaused(player)) {
        adapter.play(player);
      } else {
        adapter.pause(player);
      }
      sync();
    });

    adapter.onChange(player, sync);
  };

  const initWrapper = (wrapper) => {
    const adapter = ADAPTERS[wrapper.dataset.tdMotion];
    const toggle = wrapper.querySelector(SELECTORS.toggle);
    if (!adapter || !toggle || wrapper.dataset.tdMotionBound) return;
    wrapper.dataset.tdMotionBound = 'true';

    let attempts = 0;
    const tryBind = () => {
      const player = adapter.resolve(wrapper);
      if (player) {
        bind(wrapper, toggle, adapter, player);
      } else if (++attempts < POLL_MAX_ATTEMPTS) {
        setTimeout(tryBind, POLL_INTERVAL);
      } else {
        // Nothing is auto-moving (e.g. autoplay turned off), so the button has no job.
        toggle.hidden = true;
      }
    };
    tryBind();
  };

  const init = (root = document) => root.querySelectorAll(SELECTORS.wrapper).forEach(initWrapper);

  init();

  // Re-initialise when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
